import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaArrowLeft, FaArrowRight, FaCheck } from "react-icons/fa";

const PAD = 8;          // breathing room around the spotlit element
const GAP = 12;         // distance from the element to the tooltip
const CARD_W = 340;
const MARGIN = 16;      // keep the tooltip this far from the viewport edge

// Steps whose target isn't on the page are dropped rather than shown against
// empty space — controls can be role-gated, and lists can be empty.
const resolveSteps = (steps) =>
  (steps || []).filter((step) => {
    if (!step.target) return true;
    try {
      return document.querySelector(step.target) !== null;
    } catch {
      return false;
    }
  });

// Places the tooltip on the preferred side, flipping when there isn't room.
function positionCard(rect, placement, cardH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    return { top: Math.max(MARGIN, vh / 2 - cardH / 2), left: vw / 2 - CARD_W / 2 };
  }

  const fits = {
    bottom: vh - rect.bottom - GAP - MARGIN >= cardH,
    top: rect.top - GAP - MARGIN >= cardH,
    right: vw - rect.right - GAP - MARGIN >= CARD_W,
    left: rect.left - GAP - MARGIN >= CARD_W,
  };
  const order = [placement || "bottom", "bottom", "top", "right", "left"];
  const side = order.find((s) => fits[s]) || "bottom";

  let top;
  let left;
  if (side === "bottom" || side === "top") {
    top = side === "bottom" ? rect.bottom + GAP : rect.top - GAP - cardH;
    left = rect.left + rect.width / 2 - CARD_W / 2;
  } else {
    left = side === "right" ? rect.right + GAP : rect.left - GAP - CARD_W;
    top = rect.top + rect.height / 2 - cardH / 2;
  }

  return {
    top: Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - cardH - MARGIN)),
    left: Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - CARD_W - MARGIN)),
  };
}

export default function GuidedTour({ tour, onFinish, onDisableAll }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(180);
  const cardRef = useRef(null);

  // Freeze the step list for this run so a step's target appearing or
  // disappearing mid-tour can't renumber the steps under the user.
  const [steps] = useState(() => resolveSteps(tour?.steps));
  const step = steps[index];
  const total = steps.length;

  const finish = useCallback(() => onFinish && onFinish(), [onFinish]);

  const measure = useCallback(() => {
    if (!step?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
  }, [step]);

  // Bring the target into view, then measure where it landed.
  useEffect(() => {
    if (!step) return;
    if (step.target) {
      const el = document.querySelector(step.target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
    const t = setTimeout(measure, step.target ? 320 : 0);
    return () => clearTimeout(t);
  }, [step, measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [index, step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, total - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [finish, total]);

  if (!step || total === 0) return null;

  const isLast = index === total - 1;
  const pos = positionCard(rect, step.placement, cardH);

  return createPortal(
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label={`${tour.title} tour`}>
      {/* A single element dims the page and cuts out the target: the ring is
          drawn with an outsized box-shadow rather than four separate panels. */}
      {rect ? (
        <div
          className="absolute rounded-xl pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(15, 13, 22, 0.62)",
            outline: "2px solid #a78bfa",
            outlineOffset: "0px",
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(15, 13, 22, 0.62)" }} />
      )}

      {/* Catches clicks on the dimmed area without blocking the spotlight. */}
      <div className="absolute inset-0" onClick={finish} />

      <div
        ref={cardRef}
        className="tour-card absolute bg-white rounded-2xl shadow-2xl border border-gray-200 p-5 transition-all duration-200"
        style={{ top: pos.top, left: pos.left, width: CARD_W }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-cloudza-600">
            {tour.title}
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums flex-shrink-0">
            {index + 1} of {total}
          </span>
        </div>

        <h3 className="text-[15px] font-bold text-gray-900 mb-1.5">{step.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>

        <div className="flex items-center gap-1 mt-4 mb-4" aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === index ? "w-5 bg-cloudza-600" : "w-1.5 bg-gray-200 tour-dot-inactive"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={finish}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button onClick={() => setIndex((i) => i - 1)} className="btn-outline !py-1.5 !px-3 !text-xs">
                <FaArrowLeft /> Back
              </button>
            )}
            <button
              onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
              className="btn-primary !py-1.5 !px-4 !text-xs"
            >
              {isLast ? <FaCheck /> : <FaArrowRight />} {isLast ? "Done" : "Next"}
            </button>
          </div>
        </div>

        {onDisableAll && (
          <button
            onClick={onDisableAll}
            className="mt-3 pt-3 border-t border-gray-100 w-full text-left text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            Don't show tours automatically
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
